/**
 * EnemyAI.ts - Improved enemy behavior patterns
 *
 * This module defines configurable AI behaviors for enemies.
 * Each behavior type controls how the enemy moves, positions, and attacks.
 * It is used as a helper by Enemy, not a subclass.
 */

import { Vector2 } from '@shared/types';
import { Entity } from './Entity';

// ---------------------------------------------------------------------------
// Types & Interfaces
// ---------------------------------------------------------------------------

export type EnemyBehavior = 'melee_aggro' | 'ranged_kite' | 'caster_aoe' | 'swarm_flank' | 'boss';

export interface AIConfig {
  /** Primary behavior pattern */
  behavior: EnemyBehavior;
  /** Ideal distance from target (in world units) */
  preferredRange: number;
  /** How far from spawn point to wander while idle (in world units) */
  patrolRadius: number;
  /** Maximum distance from spawn before the enemy retreats and regens (in world units) */
  leashRange: number;
  /** Seconds to wait before chasing after first spotting the player */
  alertDelay: number;
  /** How often (in seconds) to recalculate a repositioning target */
  repositionInterval: number;
}

/**
 * Result of an AI update tick. Contains movement direction and optional state flags.
 */
export interface AIUpdateResult {
  /** Desired movement direction (unit vector or zero) */
  moveDirection: Vector2;
  /** Speed multiplier applied this frame (e.g. 0.3 for patrol) */
  speedMultiplier: number;
  /** True if the AI wants the enemy to transition to attack state */
  wantsAttack: boolean;
  /** True if the enemy should retreat to spawn */
  shouldRetreat: boolean;
  /** True if the enemy is in alert delay (spotted player but not yet chasing) */
  isAlert: boolean;
  /** True if the enemy is patrolling (idle wander) */
  isPatrolling: boolean;
  /** True if the enemy is repositioning */
  isRepositioning: boolean;
}

// ---------------------------------------------------------------------------
// Default AI configs for each behavior type
// ---------------------------------------------------------------------------

const DEFAULT_CONFIGS: Record<EnemyBehavior, AIConfig> = {
  melee_aggro: {
    behavior: 'melee_aggro',
    preferredRange: 40,
    patrolRadius: 120,
    leashRange: 600,
    alertDelay: 0,
    repositionInterval: 0,
  },
  ranged_kite: {
    behavior: 'ranged_kite',
    preferredRange: 200,  // 8-12 tiles * ~20 units per tile ~= 160-240
    patrolRadius: 100,
    leashRange: 500,
    alertDelay: 0.3,
    repositionInterval: 1.5,
  },
  caster_aoe: {
    behavior: 'caster_aoe',
    preferredRange: 150,  // 6-8 tiles
    patrolRadius: 80,
    leashRange: 500,
    alertDelay: 0.5,
    repositionInterval: 2.0,
  },
  swarm_flank: {
    behavior: 'swarm_flank',
    preferredRange: 40,
    patrolRadius: 100,
    leashRange: 600,
    alertDelay: 0,
    repositionInterval: 1.0,
  },
  boss: {
    behavior: 'boss',
    preferredRange: 60,
    patrolRadius: 50,
    leashRange: 9999,  // Bosses do not leash
    alertDelay: 0,
    repositionInterval: 0,
  },
};

/**
 * Get the default AI config for a given behavior type.
 */
export function getDefaultAIConfig(behavior: EnemyBehavior): AIConfig {
  return { ...DEFAULT_CONFIGS[behavior] };
}

// ---------------------------------------------------------------------------
// EnemyAI class
// ---------------------------------------------------------------------------

export class EnemyAI {
  public config: AIConfig;

  // Internal state
  private alertTimer: number = 0;
  private repositionTimer: number = 0;
  private patrolTarget: Vector2 | null = null;
  private patrolWaitTimer: number = 0;
  private retreatHealAccum: number = 0;

  /** Unique flanking angle for swarm enemies (set externally by GameWorld) */
  private flankAngle: number = 0;
  private flankAngleAssigned: boolean = false;

  // Stuck detection
  private lastPos: Vector2 = { x: 0, y: 0 };
  private stuckTimer: number = 0;
  private stuckJitterAngle: number = 0;
  private stuckRecoveryTimer: number = 0;
  private static readonly STUCK_THRESHOLD = 2.0; // seconds without movement
  private static readonly STUCK_MOVE_THRESHOLD = 3.0; // minimum pixels moved
  private static readonly STUCK_RECOVERY_DURATION = 0.5; // jitter for this long

  constructor(config: AIConfig) {
    this.config = { ...config };
    // Randomize initial patrol wait so enemies don't all move in sync
    this.patrolWaitTimer = Math.random() * 2;
  }

  /**
   * Assign a flank angle for swarm behavior. Called externally when
   * multiple swarm enemies are present around the same target.
   */
  setFlankAngle(angle: number): void {
    this.flankAngle = angle;
    this.flankAngleAssigned = true;
  }

  /**
   * Core update. Computes the desired movement direction and state transitions
   * based on the enemy's current state, position, and target.
   *
   * @param enemyPos  Current enemy world position
   * @param spawnPos  The enemy's original spawn position
   * @param target    The player entity (or null if no target)
   * @param aggroRange The detection range
   * @param attackRange The attack range
   * @param deltaTime Frame delta time in seconds
   * @param isChasing Whether the enemy is currently in chase state
   * @param isAttacking Whether the enemy is currently attacking
   * @param alliesPositions Positions of nearby same-type enemies (for swarm behavior)
   */
  update(
    enemyPos: Vector2,
    spawnPos: Vector2,
    target: Entity | null,
    aggroRange: number,
    attackRange: number,
    deltaTime: number,
    isChasing: boolean,
    isAttacking: boolean,
    alliesPositions: Vector2[] = [],
  ): AIUpdateResult {
    const result: AIUpdateResult = {
      moveDirection: { x: 0, y: 0 },
      speedMultiplier: 1.0,
      wantsAttack: false,
      shouldRetreat: false,
      isAlert: false,
      isPatrolling: false,
      isRepositioning: false,
    };

    // --- Leash check ---
    const dxSpawn = enemyPos.x - spawnPos.x;
    const dySpawn = enemyPos.y - spawnPos.y;
    const distFromSpawnSq = dxSpawn * dxSpawn + dySpawn * dySpawn;
    const leashSq = this.config.leashRange * this.config.leashRange;

    if (distFromSpawnSq > leashSq) {
      result.shouldRetreat = true;
      // Direction back to spawn
      const distFromSpawn = Math.sqrt(distFromSpawnSq);
      if (distFromSpawn > 0) {
        result.moveDirection = {
          x: -dxSpawn / distFromSpawn,
          y: -dySpawn / distFromSpawn,
        };
      }
      result.speedMultiplier = 1.0;
      return result;
    }

    // --- Stuck detection ---
    const moveDx = enemyPos.x - this.lastPos.x;
    const moveDy = enemyPos.y - this.lastPos.y;
    const movedDist = Math.sqrt(moveDx * moveDx + moveDy * moveDy);

    if (movedDist < EnemyAI.STUCK_MOVE_THRESHOLD && (isChasing || isAttacking)) {
      this.stuckTimer += deltaTime;
    } else {
      this.stuckTimer = 0;
      this.stuckRecoveryTimer = 0;
    }
    this.lastPos.x = enemyPos.x;
    this.lastPos.y = enemyPos.y;

    // If stuck, apply random jitter movement to escape
    if (this.stuckTimer >= EnemyAI.STUCK_THRESHOLD) {
      if (this.stuckRecoveryTimer <= 0) {
        this.stuckJitterAngle = Math.random() * Math.PI * 2;
        this.stuckRecoveryTimer = EnemyAI.STUCK_RECOVERY_DURATION;
        this.patrolTarget = null; // reset patrol target
      }
      this.stuckRecoveryTimer -= deltaTime;
      result.moveDirection = {
        x: Math.cos(this.stuckJitterAngle),
        y: Math.sin(this.stuckJitterAngle),
      };
      result.speedMultiplier = 1.2;
      if (this.stuckRecoveryTimer <= 0) {
        this.stuckTimer = 0; // reset and try normal AI again
      }
      return result;
    }

    // --- No target: patrol ---
    if (!target || !target.isAlive) {
      return this.updatePatrol(enemyPos, spawnPos, deltaTime, result);
    }

    // --- Distance to target (squared) ---
    const dx = target.position.x - enemyPos.x;
    const dy = target.position.y - enemyPos.y;
    const distSq = dx * dx + dy * dy;
    const dist = Math.sqrt(distSq);

    // --- Not yet aggroed: check aggro range ---
    if (!isChasing && !isAttacking) {
      if (distSq > aggroRange * aggroRange) {
        return this.updatePatrol(enemyPos, spawnPos, deltaTime, result);
      }

      // Alert delay
      if (this.config.alertDelay > 0 && this.alertTimer < this.config.alertDelay) {
        this.alertTimer += deltaTime;
        result.isAlert = true;
        return result;
      }
    }

    // Reset alert timer once chasing
    this.alertTimer = 0;

    // --- Behavior-specific chase/positioning ---
    switch (this.config.behavior) {
      case 'melee_aggro':
        return this.updateMeleeAggro(dx, dy, dist, attackRange, result);

      case 'ranged_kite':
        return this.updateRangedKite(dx, dy, dist, attackRange, deltaTime, result);

      case 'caster_aoe':
        return this.updateCasterAoe(dx, dy, dist, attackRange, deltaTime, result);

      case 'swarm_flank':
        return this.updateSwarmFlank(
          enemyPos, dx, dy, dist, attackRange, target, deltaTime, alliesPositions, result
        );

      case 'boss':
        return this.updateBoss(dx, dy, dist, attackRange, result);

      default:
        return this.updateMeleeAggro(dx, dy, dist, attackRange, result);
    }
  }

  /**
   * Called when the enemy is retreating to spawn. Returns HP regen amount per tick.
   * Enemies regen 10% max HP per second while retreating.
   */
  getRetreatHealAmount(maxHp: number, deltaTime: number): number {
    this.retreatHealAccum += maxHp * 0.10 * deltaTime;
    const healAmount = Math.floor(this.retreatHealAccum);
    if (healAmount > 0) {
      this.retreatHealAccum -= healAmount;
    }
    return healAmount;
  }

  /**
   * Reset retreat heal accumulator when retreat ends.
   */
  resetRetreat(): void {
    this.retreatHealAccum = 0;
  }

  // -----------------------------------------------------------------------
  // Behavior implementations
  // -----------------------------------------------------------------------

  /**
   * Melee aggro: Rush straight at the target. Attack when in range.
   */
  private updateMeleeAggro(
    dx: number, dy: number, dist: number,
    attackRange: number,
    result: AIUpdateResult,
  ): AIUpdateResult {
    if (dist <= attackRange) {
      result.wantsAttack = true;
      result.moveDirection = { x: 0, y: 0 };
    } else if (dist > 0) {
      result.moveDirection = { x: dx / dist, y: dy / dist };
    }
    return result;
  }

  /**
   * Ranged kite: Maintain 8-12 tile distance. Back away if player gets close.
   */
  private updateRangedKite(
    dx: number, dy: number, dist: number,
    attackRange: number,
    deltaTime: number,
    result: AIUpdateResult,
  ): AIUpdateResult {
    const idealMin = this.config.preferredRange * 0.8; // ~160
    const idealMax = this.config.preferredRange * 1.2; // ~240

    if (dist < idealMin) {
      // Too close: back away
      if (dist > 0) {
        result.moveDirection = { x: -dx / dist, y: -dy / dist };
      }
      result.speedMultiplier = 1.2; // Flee slightly faster
    } else if (dist > idealMax && dist > attackRange) {
      // Too far: get closer
      if (dist > 0) {
        result.moveDirection = { x: dx / dist, y: dy / dist };
      }
    } else if (dist <= attackRange) {
      // In attack range: attack while strafing slightly
      result.wantsAttack = true;
      this.repositionTimer += deltaTime;
      if (this.repositionTimer >= this.config.repositionInterval) {
        this.repositionTimer = 0;
        result.isRepositioning = true;
        // Strafe perpendicular
        const perpDir = Math.random() > 0.5 ? 1 : -1;
        if (dist > 0) {
          result.moveDirection = {
            x: (-dy / dist) * perpDir,
            y: (dx / dist) * perpDir,
          };
        }
        result.speedMultiplier = 0.6;
      }
    } else {
      // In ideal range but outside attack range: approach slowly
      if (dist > 0) {
        result.moveDirection = { x: dx / dist, y: dy / dist };
      }
      result.speedMultiplier = 0.5;
    }
    return result;
  }

  /**
   * Caster AOE: Stay at medium range, periodically reposition.
   */
  private updateCasterAoe(
    dx: number, dy: number, dist: number,
    attackRange: number,
    deltaTime: number,
    result: AIUpdateResult,
  ): AIUpdateResult {
    const idealDist = this.config.preferredRange;
    const margin = 40;

    if (dist < idealDist - margin) {
      // Too close: back up
      if (dist > 0) {
        result.moveDirection = { x: -dx / dist, y: -dy / dist };
      }
    } else if (dist > idealDist + margin && dist > attackRange) {
      // Too far: approach
      if (dist > 0) {
        result.moveDirection = { x: dx / dist, y: dy / dist };
      }
    } else {
      // In zone: attack and reposition periodically
      result.wantsAttack = dist <= attackRange;
      this.repositionTimer += deltaTime;
      if (this.repositionTimer >= this.config.repositionInterval) {
        this.repositionTimer = 0;
        result.isRepositioning = true;
        // Move to a random point at ideal distance
        const angle = Math.random() * Math.PI * 2;
        result.moveDirection = { x: Math.cos(angle), y: Math.sin(angle) };
        result.speedMultiplier = 0.5;
      }
    }
    return result;
  }

  /**
   * Swarm flank: Move in packs, surround the player.
   */
  private updateSwarmFlank(
    enemyPos: Vector2,
    dx: number, dy: number, dist: number,
    attackRange: number,
    target: Entity,
    deltaTime: number,
    alliesPositions: Vector2[],
    result: AIUpdateResult,
  ): AIUpdateResult {
    // Assign a flank angle if not yet assigned
    if (!this.flankAngleAssigned) {
      // Distribute evenly around the target based on ally count
      const allyCount = Math.max(1, alliesPositions.length + 1);
      const baseAngle = Math.atan2(
        enemyPos.y - target.position.y,
        enemyPos.x - target.position.x,
      );
      // Find our index among nearby allies
      let myIndex = 0;
      for (const ally of alliesPositions) {
        const allyAngle = Math.atan2(
          ally.y - target.position.y,
          ally.x - target.position.x,
        );
        if (allyAngle < baseAngle) myIndex++;
      }
      this.flankAngle = (myIndex / allyCount) * Math.PI * 2;
      this.flankAngleAssigned = true;
    }

    this.repositionTimer += deltaTime;

    // Target position: flank point around the target
    const flankDist = attackRange * 0.9;
    const flankX = target.position.x + Math.cos(this.flankAngle) * flankDist;
    const flankY = target.position.y + Math.sin(this.flankAngle) * flankDist;

    const toFlankX = flankX - enemyPos.x;
    const toFlankY = flankY - enemyPos.y;
    const toFlankDist = Math.sqrt(toFlankX * toFlankX + toFlankY * toFlankY);

    if (dist <= attackRange) {
      result.wantsAttack = true;
      // Continue to drift toward flank position
      if (toFlankDist > 10) {
        result.moveDirection = { x: toFlankX / toFlankDist, y: toFlankY / toFlankDist };
        result.speedMultiplier = 0.4;
      }
    } else if (toFlankDist > 5) {
      result.moveDirection = { x: toFlankX / toFlankDist, y: toFlankY / toFlankDist };
    } else if (dist > 0) {
      // At flank position but out of attack range: approach target directly
      result.moveDirection = { x: dx / dist, y: dy / dist };
    }

    // Slowly rotate flank angle
    if (this.repositionTimer >= this.config.repositionInterval) {
      this.repositionTimer = 0;
      this.flankAngle += (Math.random() * 0.5 - 0.25);
      result.isRepositioning = true;
    }

    return result;
  }

  /**
   * Boss behavior: Simple chase, let BossAI handle attack selection.
   */
  private updateBoss(
    dx: number, dy: number, dist: number,
    attackRange: number,
    result: AIUpdateResult,
  ): AIUpdateResult {
    if (dist <= attackRange) {
      result.wantsAttack = true;
    } else if (dist > 0) {
      result.moveDirection = { x: dx / dist, y: dy / dist };
    }
    return result;
  }

  /**
   * Patrol behavior: Wander randomly within patrolRadius of spawn.
   */
  private updatePatrol(
    enemyPos: Vector2,
    spawnPos: Vector2,
    deltaTime: number,
    result: AIUpdateResult,
  ): AIUpdateResult {
    result.isPatrolling = true;
    result.speedMultiplier = 0.3; // Slow patrol speed

    // Wait between patrol movements
    if (this.patrolWaitTimer > 0) {
      this.patrolWaitTimer -= deltaTime;
      result.moveDirection = { x: 0, y: 0 };
      return result;
    }

    // Pick a new patrol target if needed
    if (!this.patrolTarget) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * this.config.patrolRadius;
      this.patrolTarget = {
        x: spawnPos.x + Math.cos(angle) * dist,
        y: spawnPos.y + Math.sin(angle) * dist,
      };
    }

    // Move toward patrol target
    const pdx = this.patrolTarget.x - enemyPos.x;
    const pdy = this.patrolTarget.y - enemyPos.y;
    const pDistSq = pdx * pdx + pdy * pdy;

    if (pDistSq < 15 * 15) {
      // Reached target, wait and pick a new one
      this.patrolTarget = null;
      this.patrolWaitTimer = 1 + Math.random() * 3;
      result.moveDirection = { x: 0, y: 0 };
    } else {
      const pDist = Math.sqrt(pDistSq);
      result.moveDirection = { x: pdx / pDist, y: pdy / pDist };
    }

    return result;
  }
}
